import { useState, useEffect, useContext } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  Container,
  InputAdornment,
  Dialog,
  DialogContent,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useForm } from "react-hook-form";
import * as constants from "../../../constants";
import { useHistory } from "react-router-dom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PassReqDialog from "../../common/PassReqDialog";
import { GlobalContext } from "../../common/Context";
import LoadingWheel from "../../common/LoadingWheel";
import { useTranslation } from "react-i18next";

const ResetPass = () => {
  const { t } = useTranslation("Account");
  const { firebaseState, mobileView, signUpEmail, keyHash } =
    useContext(GlobalContext);

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors, isSubmitSuccessful },
    reset,
  } = useForm();
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [textType, setTextType] = useState("password");
  const [isOpen, setIsOpen] = useState(false);
  const [isDOpen, setIsDOpen] = useState(false);
  const history = useHistory();

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    width: mobileView ? "20rem" : "40rem",
  };
  const inputBoxStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    mt: "1rem",
  };
  const titleBoxStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  };
  const inputStyle = {
    mb: "1rem",
    width: "20rem",
  };
  const buttonStyle = {
    width: "20rem",
  };
  const errorStyle = {
    mb: ".5rem",
  };
  const modalTitleStyle = {
    mb: "1rem",
  };
  const modalBoxStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    bgcolor: "background.paper",
    p: 4,
  };
  const helpIconStyle = {
    fontSize: ".75rem",
    ml: "1rem",
    opacity: "0.5",
  };

  const handleHelpClick = () => {
    setIsDOpen(!isDOpen);
  };

  const handlePassMatch = async (data) => {
    try {
      const result = await firebaseState.resetPass(
        data.email,
        data.password,
        keyHash,
      );
      if (result) {
        setIsLoading(false);
        setIsDisabled(false);
        setIsOpen(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handlePassNotMatch = () => {
    setIsLoading(false);
    setError("repassword", { message: t("passwordsMustMatch") });
    setIsDisabled(false);
  };

  const handleClick = async (data) => {
    setIsDisabled(true);
    setIsLoading(true);
    getValues("password") !== getValues("repassword")
      ? handlePassNotMatch()
      : await handlePassMatch(data);
  };

  const handleIconClick = (type) => {
    type === "password" ? setTextType("text") : setTextType("password");
  };

  const handleOkClick = () => {
    setIsOpen(false);
    history.push(constants.SIGNIN_PATH);
  };

  useEffect(() => {
    isSubmitSuccessful && reset({ email: "", password: "", repassword: "" });
  }, [isSubmitSuccessful, reset]);

  // Get rid of email and inviteid from location state once page is loaded so they don't persist
  useEffect(() => {
    window.history.replaceState({}, document.title);
  }, []);

  return (
    <Container sx={containerStyle}>
      <img
        src="images\BI-Tool_LogIn-Graphic-01.png"
        alt="BI Tool Login"
        width={mobileView ? "150rem" : "250rem"}
      />
      <Typography color="primary" variant="h1">
        {t("title")}
      </Typography>
      <Box sx={titleBoxStyle}>
        <Typography color="primary" variant="h3">
          {t("resetPassword")}
          <HelpOutlineIcon onClick={handleHelpClick} sx={helpIconStyle} />
        </Typography>
      </Box>
      {isLoading ? (
        <LoadingWheel width={4} />
      ) : (
        <>
          <form>
            <Box sx={inputBoxStyle}>
              <TextField
                label={t("email")}
                sx={inputStyle}
                color="#9B57EC"
                value={signUpEmail}
                size="small"
                {...register("email", {
                  required: t("fieldRequired"),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("incorrectEmailFormat"),
                  },
                })}
              />
              <Typography sx={errorStyle} variant="error">
                {errors.email?.message}
              </Typography>
              <TextField
                type={textType}
                label={t("password")}
                size="small"
                color="#9B57EC"
                sx={inputStyle}
                {...register("password", {
                  required: t("fieldRequired"),
                  pattern: {
                    value:
                      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
                    message: t("incorrectPasswordFormat"),
                  },
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment>
                      <VisibilityIcon
                        onClick={() => handleIconClick(textType)}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography sx={errorStyle} variant="error">
                {errors.password?.message}
              </Typography>
              <TextField
                type={textType}
                label={t("confirmPassword")}
                size="small"
                color="#9B57EC"
                sx={inputStyle}
                {...register("repassword", {
                  required: t("fieldRequired"),
                })}
              />
              <Typography sx={errorStyle} variant="error">
                {errors.repassword?.message}
              </Typography>
              <Button
                color="info"
                disabled={isDisabled}
                sx={buttonStyle}
                variant="contained"
                onClick={handleSubmit(handleClick)}>
                {t("setPassword")}
              </Button>
            </Box>
          </form>
        </>
      )}
      <PassReqDialog isOpen={isDOpen} setIsOpen={setIsDOpen} />
      <Dialog open={isOpen}>
        <DialogContent sx={modalBoxStyle}>
          <Typography sx={modalTitleStyle} color="primary" variant="h3">
            {t("passwordReset")}
          </Typography>
          <Typography color="primary" variant="body1">
            {t("passwordResetDesc")}
          </Typography>
          <Button
            onClick={handleOkClick}
            sx={buttonStyle}
            color="info"
            variant="contained">
            {t("ok")}
          </Button>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ResetPass;
